import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Pencil, Trash2, Plus, Calendar as CalendarIcon, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available cities (sync with location filter)
const CITIES = [
  'Bengaluru',
  'Delhi',
  'Gurgaon', 
  'Mumbai',
  'Hyderabad',
  'Pune',
  'Chennai',
  'Kolkata'
];

export default function EventsManager({ credentials, listingToApprove, onListingProcessed }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [selectedL1Category, setSelectedL1Category] = useState('');
  const [cities, setCities] = useState(CITIES);
  const [isAddCityDialogOpen, setIsAddCityDialogOpen] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    l1_category: '',
    l2_category: '',
    location: '',
    city: 'Bangalore',
    lat: '',
    lng: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    type: 'open',
    price: '',
    requiresRegistration: 'false',
    registrationUrl: '',
    isRecurring: false,
    recurringDays: []
  });

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    if (listingToApprove) {
      setEditingEvent(null);
      
      setFormData({
        title: listingToApprove.title,
        description: listingToApprove.description,
        category: listingToApprove.category,
        l1_category: listingToApprove.l1_category || '',
        l2_category: listingToApprove.l2_category || '',
        icon: listingToApprove.icon || '',
        location: listingToApprove.location,
        city: listingToApprove.city || 'Bangalore',
        lat: '', // Admin needs to fill
        lng: '', // Admin needs to fill
        eventDate: listingToApprove.eventDate,
        startTime: listingToApprove.startTime,
        endTime: listingToApprove.endTime,
        type: listingToApprove.type,
        price: listingToApprove.price,
        requiresRegistration: listingToApprove.requiresRegistration.toString(),
        registrationUrl: listingToApprove.registrationUrl || '',
        isRecurring: listingToApprove.isRecurring || false,
        recurringDays: listingToApprove.recurringDays || []
      });
      
      if (listingToApprove.l1_category) {
        setSelectedL1Category(listingToApprove.l1_category);
      }
      
      if (listingToApprove.eventDate && !listingToApprove.isRecurring) {
        setSelectedDate(new Date(listingToApprove.eventDate));
      }
      
      setIsDialogOpen(true);
      
      // Notify parent that listing has been processed
      if (onListingProcessed) {
        onListingProcessed();
      }
    }
  }, [listingToApprove, onListingProcessed]);

  const fetchEvents = async () => {
    try {
      console.log('🔄 Fetching events from:', `${BACKEND_URL}/api/admin/events`);
      const response = await fetch(`${BACKEND_URL}/api/admin/events`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });
      console.log('📥 Fetch response status:', response.status);
      const data = await response.json();
      console.log('📥 Fetched events count:', data.length);
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔍 DEBUGGING UPDATE EVENT');
    console.log('Backend URL:', BACKEND_URL);
    console.log('Editing event:', editingEvent);
    console.log('Form data:', formData);
    
    // Validate recurring event has days selected
    if (formData.isRecurring && (!formData.recurringDays || formData.recurringDays.length === 0)) {
      toast.error('Please select at least one day for recurring event');
      return;
    }
    
    // Validate non-recurring event has date
    if (!formData.isRecurring && !formData.eventDate) {
      toast.error('Please select an event date');
      return;
    }
    
    // Validate registration URL only if registration is required
    if (formData.requiresRegistration === 'true' && !formData.registrationUrl) {
      toast.error('Please provide a registration URL');
      return;
    }
    
    const eventData = {
      title: formData.title,
      description: formData.description,
      category: formData.category || formData.l1_category, // Keep for backward compatibility
      l1_category: formData.l1_category,
      l2_category: formData.l2_category,
      icon: formData.icon,
      position: {
        lat: parseFloat(formData.lat),
        lng: parseFloat(formData.lng)
      },
      location: formData.location,
      city: formData.city,
      eventDate: formData.eventDate || new Date().toISOString().split('T')[0], // Placeholder for recurring events
      startTime: formData.startTime,
      endTime: formData.endTime,
      type: formData.type,
      price: formData.price,
      requiresRegistration: formData.requiresRegistration === 'true',
      registrationUrl: formData.requiresRegistration === 'true' ? formData.registrationUrl : '',
      isRecurring: formData.isRecurring,
      recurringDays: formData.recurringDays
    };

    try {
      const url = editingEvent 
        ? `${BACKEND_URL}/api/admin/events/${editingEvent._id}`
        : `${BACKEND_URL}/api/admin/events`;
      
      const method = editingEvent ? 'PUT' : 'POST';
      
      console.log('🚀 Making request to:', url);
      console.log('🚀 Method:', method);
      console.log('🚀 Auth header:', 'Basic ' + btoa(`${credentials.username}:${credentials.password}`));
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        },
        body: JSON.stringify(eventData)
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Success result:', result);
        toast.success(`Event ${editingEvent ? 'updated' : 'created'} successfully`);
        setIsDialogOpen(false);
        fetchEvents();
      } else {
        const errorText = await response.text();
        console.log('❌ Error response:', errorText);
        toast.error(`Failed to ${editingEvent ? 'update' : 'create'} event: ${response.status}`);
      }
    } catch (error) {
      console.error('💥 Network error:', error);
      toast.error('Network error occurred');
    }
  };

  const handleDelete = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        toast.success('Event deleted');
        fetchEvents();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('An error occurred');
    }
  };

  // Normalize time to HH:MM format for HTML time input
  const normalizeTimeFormat = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length !== 2) return timeStr;
    const hours = parts[0].padStart(2, '0');
    const minutes = parts[1].padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      category: event.category,
      l1_category: event.l1_category || '',
      l2_category: event.l2_category || '',
      icon: event.icon || '',
      location: event.location,
      city: event.city || 'Bangalore',
      lat: event.position.lat.toString(),
      lng: event.position.lng.toString(),
      eventDate: event.eventDate,
      startTime: normalizeTimeFormat(event.startTime),
      endTime: normalizeTimeFormat(event.endTime),
      type: event.type,
      price: event.price,
      requiresRegistration: event.requiresRegistration.toString(),
      registrationUrl: event.registrationUrl,
      isRecurring: event.isRecurring || false,
      recurringDays: event.recurringDays || []
    });
    if (event.l1_category) {
      setSelectedL1Category(event.l1_category);
    }
    if (event.eventDate && !event.isRecurring) {
      setSelectedDate(new Date(event.eventDate));
    }
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingEvent(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      l1_category: '',
      l2_category: '',
      icon: '',
      location: '',
      city: 'Bangalore',
      lat: '',
      lng: '',
      eventDate: '',
      startTime: '',
      endTime: '',
      type: 'open',
      price: '',
      requiresRegistration: 'false',
      registrationUrl: '',
      isRecurring: false,
      recurringDays: []
    });
    setSelectedL1Category('');
    setSelectedDate(null);
  };

  const formatEventDateTime = (event) => {
    try {
      const date = new Date(event.eventDate);
      const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      return `${dateStr}, ${event.startTime} - ${event.endTime}`;
    } catch {
      return 'Date not set';
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/events/export`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `events-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Events exported successfully');
      } else {
        toast.error('Failed to export events');
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('An error occurred while exporting');
    }
  };

  const handleUploadCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);

    try {
      const fileContent = await file.text();
      
      const response = await fetch(`${BACKEND_URL}/api/admin/events/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        },
        body: JSON.stringify({ file_content: fileContent })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to import events');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error('An error occurred while importing');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading events...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Events Management</h2>
        <div className="flex space-x-2">
          <Button onClick={handleDownloadCSV} variant="outline" disabled={events.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            onClick={() => fileInputRef.current?.click()} 
            variant="outline" 
            disabled={uploading}
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Uploading...' : 'Import CSV'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleUploadCSV}
            style={{ display: 'none' }}
          />
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} className="gradient-primary text-primary-foreground">
                <Plus className="h-4 w-4 mr-2" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="grid grid-cols-2 gap-4 overflow-y-auto scrollbar-admin flex-1 pr-2">
                  <div className="col-span-2">
                    <Label htmlFor="title">Event Title *</Label>
                    <Input 
                      id="title" 
                      required 
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea 
                      id="description" 
                      required 
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3} 
                    />
                  </div>

                  <div>
                    <Label htmlFor="l1_category">L1 Category *</Label>
                    <Select 
                      required 
                      value={formData.l1_category}
                      onValueChange={(value) => {
                        setFormData({...formData, l1_category: value, l2_category: ''});
                        setSelectedL1Category(value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select L1 category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat._id} value={cat._id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="l2_category">L2 Category *</Label>
                    <Select 
                      required 
                      value={formData.l2_category}
                      onValueChange={(value) => {
                        const selectedCat = categories.find(c => c._id === formData.l1_category);
                        const selectedL2 = selectedCat?.l2_categories?.find(l2 => l2.id === value);
                        setFormData({
                          ...formData, 
                          l2_category: value,
                          icon: selectedL2?.emoji || formData.icon
                        });
                      }}
                      disabled={!formData.l1_category}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select L2 category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories
                          .find(c => c._id === formData.l1_category)
                          ?.l2_categories?.map((l2) => (
                            <SelectItem key={l2.id} value={l2.id}>
                              {l2.emoji} {l2.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="type">Event Type *</Label>
                    <Select 
                      required 
                      value={formData.type}
                      onValueChange={(value) => setFormData({...formData, type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="invite-only">Invite Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Input 
                      id="location" 
                      required 
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="e.g., Koramangala"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city">City *</Label>
                    <div className="flex gap-2">
                      <Select 
                        required 
                        value={formData.city}
                        onValueChange={(value) => {
                          if (value === 'add_new') {
                            setIsAddCityDialogOpen(true);
                          } else {
                            setFormData({...formData, city: value});
                          }
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                        <SelectContent>
                          {cities.map((city) => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                          <SelectItem value="Others">Others</SelectItem>
                          <SelectItem value="add_new" className="text-primary font-medium">
                            + Add New City
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="lat">Latitude *</Label>
                    <Input 
                      id="lat" 
                      type="number" 
                      step="any" 
                      required 
                      value={formData.lat}
                      onChange={(e) => setFormData({...formData, lat: e.target.value})}
                      placeholder="12.9352" 
                    />
                  </div>

                  <div>
                    <Label htmlFor="lng">Longitude *</Label>
                    <Input 
                      id="lng" 
                      type="number" 
                      step="any" 
                      required 
                      value={formData.lng}
                      onChange={(e) => setFormData({...formData, lng: e.target.value})}
                      placeholder="77.6245" 
                    />
                  </div>

                  <div className="col-span-2">
                    <Label>Event Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          disabled={formData.isRecurring}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.isRecurring ? 'Recurring Event' : (selectedDate ? format(selectedDate, 'PPP') : 'Pick a date')}
                        </Button>
                      </PopoverTrigger>
                      {!formData.isRecurring && (
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => {
                              setSelectedDate(date);
                              setFormData({...formData, eventDate: format(date, 'yyyy-MM-dd')});
                            }}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          />
                        </PopoverContent>
                      )}
                    </Popover>
                  </div>

                  <div className="col-span-2">
                    <Label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.isRecurring}
                        onChange={(e) => {
                          setFormData({
                            ...formData, 
                            isRecurring: e.target.checked,
                            recurringDays: e.target.checked ? formData.recurringDays : []
                          });
                          if (!e.target.checked) {
                            setSelectedDate(null);
                          }
                        }}
                        className="h-4 w-4"
                      />
                      <span>Recurring Event</span>
                    </Label>
                    {formData.isRecurring && (
                      <div className="mt-2 space-y-2">
                        <p className="text-sm text-muted-foreground">Select days:</p>
                        <div className="flex flex-wrap gap-2">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                            <Button
                              key={day}
                              type="button"
                              variant={formData.recurringDays.includes(day) ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                const days = formData.recurringDays.includes(day)
                                  ? formData.recurringDays.filter(d => d !== day)
                                  : [...formData.recurringDays, day];
                                setFormData({...formData, recurringDays: days});
                              }}
                            >
                              {day}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="startTime">Start Time *</Label>
                    <Input 
                      id="startTime" 
                      type="time" 
                      required 
                      value={formData.startTime}
                      onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="endTime">End Time *</Label>
                    <Input 
                      id="endTime" 
                      type="time" 
                      required 
                      value={formData.endTime}
                      onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="price">Price *</Label>
                    <Input 
                      id="price" 
                      required 
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      placeholder="Free or ₹500" 
                    />
                  </div>

                  <div>
                    <Label htmlFor="requiresRegistration">Registration Required *</Label>
                    <Select 
                      required 
                      value={formData.requiresRegistration}
                      onValueChange={(value) => {
                        setFormData({
                          ...formData, 
                          requiresRegistration: value,
                          registrationUrl: value === 'false' ? '' : formData.registrationUrl
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.requiresRegistration === 'true' && (
                    <div className="col-span-2">
                      <Label htmlFor="registrationUrl">Registration URL *</Label>
                      <Input 
                        id="registrationUrl" 
                        type="url" 
                        required 
                        value={formData.registrationUrl}
                        onChange={(e) => setFormData({...formData, registrationUrl: e.target.value})}
                        placeholder="https://example.com" 
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-between space-x-2 pt-4 border-t mt-4 bg-background sticky bottom-0">
                  <div>
                    {editingEvent && (
                      <Button 
                        type="button" 
                        variant="destructive" 
                        onClick={() => {
                          if (confirm('Are you sure you want to delete this event?')) {
                            handleDelete(editingEvent._id);
                            setIsDialogOpen(false);
                          }
                        }}
                      >
                        Delete Event
                      </Button>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="gradient-primary text-primary-foreground">
                      {editingEvent ? 'Update' : 'Create'} Event
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="max-h-[calc(100vh-250px)] overflow-y-auto scrollbar-admin pr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <Card key={event._id} className="relative">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{event.icon}</span>
                  <CardTitle className="text-lg">{event.title}</CardTitle>
                </div>
                <div className="flex space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEditDialog(event)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(event._id)} className="text-destructive hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="capitalize px-2 py-1 bg-muted rounded">{event.category}</span>
                <span className="capitalize px-2 py-1 bg-muted rounded">{event.type === 'invite-only' ? 'Invite Only' : 'Open'}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                <div>📍 {event.location}</div>
                <div>📅 {formatEventDateTime(event)}</div>
                <div>💵 {event.price}</div>
              </div>
            </CardContent>
          </Card>
        ))}
        </div>
      </div>

      {events.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">No events yet. Create your first event!</p>
          </CardContent>
        </Card>
      )}

      {/* Add City Dialog */}
      <Dialog open={isAddCityDialogOpen} onOpenChange={setIsAddCityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New City</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>City Name</Label>
              <Input
                placeholder="e.g., Jaipur"
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
              />
            </div>
            <Button 
              onClick={() => {
                if (newCityName.trim()) {
                  setCities([...cities, newCityName.trim()]);
                  setFormData({...formData, city: newCityName.trim()});
                  setNewCityName('');
                  setIsAddCityDialogOpen(false);
                  toast.success(`Added ${newCityName} to cities list`);
                }
              }}
              className="w-full"
            >
              Add City
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
